// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { LabelAutoSize } from '@Primitive/Typography';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import type { LocalizationMap } from '@Primitive/Localization/Localization';

// TODO: use settingslist component
// =======================================================================================================
export interface DecorationLabelData {
    DecorationLabelSettings: DecorationLabelSettings;
    MaxFontSize: number;
    TextAlignment: "center" | "left" | "right";
    FontBold: boolean;
    FontItalic: boolean;
}
// -------------------------------------------------------------
export interface DecorationLabelSettings {
    Value: string;
    BackgroundColor: string;
    TextColor: string;
}
// -------------------------------------------------------------
class DecorationLabel extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<DecorationLabelData>, {}> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<DecorationLabelData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        // compute offsets to set the value in widget's center
        let minValue, maxValue: number;
        const { width, height, LocalizationMap, WidgetData, PortsStates } = this.props;
        let space = 1;
        if (width > height)
            minValue = height;
        else
            minValue = width;

        const DecorationLabelSettings: DecorationLabelSettings = WidgetData.DecorationLabelSettings;

        let value: string;
        const port = _.get(PortsStates, dataPortKey);
        if (isDefined(port) && isDefined(port.State)) {
            value = port.State;
        }
        else {
            // translate if we don't have a port state
            value = LocalizationMap['Value'];
            if (IsNullOrEmpty(value))
                value = DecorationLabelSettings.Value;
        }

        let tvalue: React.CSSProperties = {
            width: width,
            height: height,
            backgroundColor: DecorationLabelSettings.BackgroundColor,
            color: DecorationLabelSettings.TextColor,
            position: "absolute",
            lineHeight: minValue + "px",
            fontFamily: "'Share Tech', sans-serif",
            display: "block",
            textAlign: WidgetData.TextAlignment,
            fontWeight: (WidgetData.FontBold) ? "bold" : "initial",
            fontStyle: (WidgetData.FontItalic) ? "italic" : "normal",
            overflow: "hidden",
            whiteSpace: 'nowrap',
        };
        return (
            <LabelAutoSize
                Text={value}
                Height={height - 20}
                Width={width - 20}
                MaxFontSize={WidgetData.MaxFontSize}
                Style={tvalue}
            />);
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
class DecorationLabelWidgetSettings extends React.PureComponent<IWidgetSettingsProps<DecorationLabelData>> {
    // ------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<DecorationLabelData>) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------
    render() {

        let settings: SettingsGroup<DecorationLabelData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.String,
                        label: 'Value',
                        tooltip: 'Text to be displayed.',
                        dataPath: "DecorationLabelSettings.Value",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        tooltip: 'Sets the background color of widget.',
                        dataPath: "DecorationLabelSettings.BackgroundColor",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Font Color',
                        tooltip: 'Sets the font color.',
                        dataPath: "DecorationLabelSettings.TextColor",
                    },
                    {
                        type: SettingsType.Slider,
                        label: 'Max Font Size',
                        tooltip: 'Sets the maximum font size.',
                        dataPath: "MaxFontSize",
                        maxValue: 100,
                        minValue: 9,
                        stepValue: 1,
                        valuePosfix: "px"
                    },
                    {
                        type: SettingsType.DropDown,
                        label: 'Text Alignment',
                        dataPath: "TextAlignment",
                        items: [
                            { label: "Center", value: "center" },
                            { label: "Left", value: "left" },
                            { label: "Right", value: "right" }
                        ]
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Font Bold',
                        dataPath: "FontBold"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Font Italic',
                        dataPath: "FontItalic"
                    },
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Gauge Description interface

export class DecorationLabelWidget extends IWidgetThingsClass<DecorationLabelData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <DecorationLabel />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <DecorationLabelWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = this.WidgetData;
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        this.GetPortsFromSettings(wData.DecorationLabelSettings).forEach((p) => {
            thing.AddPort(new WebThingPort(p, WebThingPortType.Data), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }

    // ------------------------------------------------------------------------------------------------
    private GetPortsFromSettings(DecorationLabelSettings: DecorationLabelSettings): Plegma.Port[] {
        const ports: Plegma.Port[] = [];

        ports.push({
            Name: "Input",
            PortKey: dataPortKey,
            Type: Plegma.ePortType.String,
            ioDirection: Plegma.ioPortDirection.Input,
            ConfFlags: Plegma.ePortConf.None,
            Description: "",
            // null on purpose
            State: null
        });

        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = this.GetPortsFromSettings(wData.DecorationLabelSettings).map((port) => new WebThingPort(port, WebThingPortType.Data));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): DecorationLabelData {
        return _.cloneDeep({
            DecorationLabelSettings: {
                TextColor: "rgba(102, 204, 102, 1)",
                BackgroundColor: "rgba(255, 255, 255, 1)",
                Value: "Test Label"
            },
            MaxFontSize: 24,
            TextAlignment: "center",
            FontItalic: false,
            FontBold: false
        });
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return DecorationLabelWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: DecorationLabelData = DecorationLabelWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Decoration Label",
            WidgetTitle: "",
            Key: "DecorationLabelWidget",
            Type: "DecorationLabelWidget",
            Data: data,
            Description: "DecorationLabel widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/decoration_label.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = {};

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        // expose label's value (if any)
        if (!IsNullOrEmpty(this.WidgetData.DecorationLabelSettings.Value)) {
            res['Value'] = this.WidgetData.DecorationLabelSettings.Value;
        }

        return res;
    };
    // ------------------------------------------------------------------------------------------------
}
const dataPortKey = "input";
// =======================================================================================================
